/**
 * Free-particle layer — cells in transit.
 *
 * Adopted from Noita's approach, which they describe as still in use today and
 * as the thing that separates convincing liquid from "a blobby thing falling
 * down": a cell given an impulse is *promoted out of the grid* into a
 * velocity-integrated free body, travels ballistically, and is *reinserted into
 * the grid* on collision.
 *
 * The critical property is **conservation**. A particle is a real cell in
 * transit, not a decorative effect. Blood sprayed from a hit is the same blood
 * that lands, pools, conducts electricity, and feeds a curse. If these were
 * cosmetic sparks, every splash would be a lie about the simulation, and the
 * whole premise of the game is that the world does not lie about matter.
 *
 * SoA, fixed capacity, zero allocation after construction.
 */

import { profiler } from '../core/profiler';
import type { Rng } from '../core/rng';
import { Category, Flag, M, VOID } from './materials';
import { CellWorld } from './world';

const CAPACITY = 4096;

/** Terminal velocity, cells/sec. Above this, sweeping gets expensive. */
const MAX_SPEED = 900;

/** Cells/sec². Matches the entity gravity so debris and bodies agree. */
const GRAVITY = 1200;

export class Particles {
  private world: CellWorld;
  private rng: Rng;

  readonly x = new Float32Array(CAPACITY);
  readonly y = new Float32Array(CAPACITY);
  readonly vx = new Float32Array(CAPACITY);
  readonly vy = new Float32Array(CAPACITY);
  readonly mat = new Uint16Array(CAPACITY);
  readonly temp = new Int16Array(CAPACITY);
  readonly flags = new Uint16Array(CAPACITY);
  readonly life = new Float32Array(CAPACITY);

  /** Dense packing: [0, count) is live. Removal swaps with the last. */
  count = 0;

  /** Particles that couldn't be spawned because the pool was full. */
  droppedThisTick = 0;

  /**
   * Work units spent this tick: one per live particle, plus one per cell its
   * collision sweep steps over.
   *
   * The sweep is why the pool size alone is not the quota. A particle at
   * `MAX_SPEED` sweeps `ceil(900/60) = 15` cells in a tick and a settling one
   * sweeps a single cell, so a full pool spans a 15× cost range and counting
   * particles would price the two the same — the denomination mistake
   * `thermal.ts` documents, one system over.
   */
  workThisTick = 0;

  /**
   * Ceiling on `workThisTick`: a full pool, every particle at terminal velocity.
   * Derived from `CAPACITY` and `MAX_SPEED`, so it moves when they do.
   */
  readonly maxWorkPerTick = CAPACITY * (1 + Math.ceil(MAX_SPEED / 60));

  constructor(world: CellWorld, rng: Rng) {
    this.world = world;
    this.rng = rng;
  }

  /**
   * Promote a grid cell into a free particle. Clears the source cell, so
   * matter is moved rather than duplicated.
   */
  promote(x: number, y: number, vx: number, vy: number): boolean {
    const m = this.world.peek(x, y);
    if (m === VOID) return false;
    // Rigid structure doesn't spray; it has to be broken into its shattered
    // form first, which is `damageCell`'s job, not ours.
    if (M.category[m] === Category.RIGID) return false;

    const t = this.world.getTemp(x, y);
    const f = this.world.getFlags(x, y);
    if (!this.spawnRaw(x + 0.5, y + 0.5, vx, vy, m, t, f)) return false;
    this.world.set(x, y, VOID);
    return true;
  }

  /**
   * Emit new matter directly as particles — the entry point for weapon
   * emissions, sprayers, and blood from a hit, where the matter does not
   * already exist in the grid.
   */
  emit(
    x: number,
    y: number,
    mat: number,
    count: number,
    speed: number,
    spread: number,
    dirX = 0,
    dirY = -1,
    temp?: number,
  ): void {
    const t = temp === undefined ? M.spawnTemp[mat] : temp;
    const base = Math.atan2(dirY, dirX);
    for (let i = 0; i < count; i++) {
      const a = base + this.rng.range(-spread, spread);
      const s = speed * this.rng.range(0.6, 1.2);
      this.spawnRaw(x, y, Math.cos(a) * s, Math.sin(a) * s, mat, t, 0);
    }
  }

  /** Radial burst — explosions, ruptures, shatters. */
  burst(x: number, y: number, mat: number, count: number, speed: number, temp?: number): void {
    this.emit(x, y, mat, count, speed, Math.PI, 1, 0, temp);
  }

  /**
   * Promote a disc of existing cells outward from a point. This is what an
   * explosion actually does to loose matter — it throws the room, rather than
   * deleting it and spawning generic debris.
   */
  scatter(cx: number, cy: number, radius: number, speed: number, maxCells = 220): number {
    let n = 0;
    const r2 = radius * radius;
    for (let y = Math.floor(cy - radius); y <= cy + radius && n < maxCells; y++) {
      for (let x = Math.floor(cx - radius); x <= cx + radius && n < maxCells; x++) {
        const dx = x - cx;
        const dy = y - cy;
        const d2 = dx * dx + dy * dy;
        if (d2 > r2 || d2 < 0.01) continue;
        const d = Math.sqrt(d2);
        const falloff = 1 - d / radius;
        const s = speed * falloff * this.rng.range(0.7, 1.3);
        if (this.promote(x, y, (dx / d) * s, (dy / d) * s)) n++;
      }
    }
    return n;
  }

  private spawnRaw(
    x: number,
    y: number,
    vx: number,
    vy: number,
    mat: number,
    temp: number,
    flags: number,
  ): boolean {
    if (this.count >= CAPACITY) {
      this.droppedThisTick++;
      return false;
    }
    const i = this.count++;
    this.x[i] = x;
    this.y[i] = y;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.mat[i] = mat;
    this.temp[i] = temp;
    // MOVED is a grid-stepping concern and must not survive the round trip.
    this.flags[i] = flags & ~Flag.MOVED;
    this.life[i] = 4;
    return true;
  }

  step(dt: number): void {
    // Its own bucket. Reporting into `cellsim` did not just misattribute the
    // cost — because the profiler averaged segments, an extra cheap segment
    // under the same key roughly *halved* the reported cell-sim average, so the
    // one system with a real quota was the one whose budget looked safest.
    profiler.begin('particles');
    this.droppedThisTick = 0;
    this.workThisTick = 0;
    const w = this.world;

    for (let i = 0; i < this.count; i++) {
      this.workThisTick++;
      const m = this.mat[i];
      const cat = M.category[m];

      // Gases and plasma rise; everything else falls. Buoyancy is just a
      // signed gravity scale, so it falls out of density like the grid does.
      const buoyancy = cat === Category.GAS || cat === Category.PLASMA ? -0.25 : 1;
      let nvx = this.vx[i];
      let nvy = this.vy[i] + GRAVITY * buoyancy * dt;

      // Air drag, so sprays fan out and slow rather than flying forever.
      const drag = cat === Category.GAS ? 2.6 : 0.4;
      const k = Math.max(0, 1 - drag * dt);
      nvx *= k;
      nvy *= k;

      const sp = Math.hypot(nvx, nvy);
      if (sp > MAX_SPEED) {
        nvx = (nvx / sp) * MAX_SPEED;
        nvy = (nvy / sp) * MAX_SPEED;
      }

      const px = this.x[i];
      const py = this.y[i];
      let nx = px + nvx * dt;
      let ny = py + nvy * dt;

      // Sweep, so a fast particle can't tunnel through a one-cell wall.
      const steps = Math.max(1, Math.ceil(Math.hypot(nx - px, ny - py)));
      this.workThisTick += steps;
      let landed = false;
      let lx = px;
      let ly = py;
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        const sx = px + (nx - px) * t;
        const sy = py + (ny - py) * t;
        const cx = Math.floor(sx);
        const cy = Math.floor(sy);
        const hit = w.peek(cx, cy);

        if (hit !== VOID && M.solid[hit] === 1) {
          // Reinsert at the last free cell along the sweep.
          this.land(i, Math.floor(lx), Math.floor(ly), nvx, nvy);
          landed = true;
          break;
        }
        lx = sx;
        ly = sy;
      }
      if (landed) {
        this.remove(i);
        i--;
        continue;
      }

      this.life[i] -= dt;
      if (this.life[i] <= 0) {
        this.land(i, Math.floor(nx), Math.floor(ny), nvx, nvy);
        this.remove(i);
        i--;
        continue;
      }

      this.x[i] = nx;
      this.y[i] = ny;
      this.vx[i] = nvx;
      this.vy[i] = nvy;
    }

    profiler.setCount('particles', this.count);
    profiler.end('particles');
  }

  /**
   * Return a particle to the grid. If the target cell is occupied, search a
   * small neighbourhood — dropping the particle instead would destroy matter,
   * and "acid vanished because the cell was busy" is exactly the kind of quiet
   * inconsistency that makes players stop trusting the simulation.
   */
  private land(i: number, cx: number, cy: number, vx: number, vy: number): void {
    const w = this.world;
    const m = this.mat[i];
    const t = this.temp[i];
    const f = this.flags[i];

    if (w.peek(cx, cy) === VOID) {
      w.set(cx, cy, m, t);
      if (f) w.addFlag(cx, cy, f & ~Flag.MOVED);
      return;
    }

    // Spiral outward looking for space. The radius is generous because the
    // alternative is destroying matter, and this class exists to promise it
    // doesn't — a splash that silently deletes acid teaches the player the
    // simulation lies.
    for (let r = 1; r <= 6; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
          if (w.peek(cx + dx, cy + dy) === VOID) {
            w.set(cx + dx, cy + dy, m, t);
            if (f) w.addFlag(cx + dx, cy + dy, f & ~Flag.MOVED);
            return;
          }
        }
      }
    }

    // Still nowhere. Rather than delete it, displace something lighter and
    // take its place — the displaced cell becomes a particle in turn, so the
    // matter is conserved by handing the problem one cell outward.
    const here = w.peek(cx, cy);
    if (here !== VOID && M.density[m] > M.density[here] && M.solid[here] === 0) {
      const dt2 = w.getTemp(cx, cy);
      const df = w.getFlags(cx, cy);
      w.set(cx, cy, m, t);
      if (f) w.addFlag(cx, cy, f & ~Flag.MOVED);
      // Re-emit the displaced material upward with a little scatter.
      this.spawnRaw(cx + 0.5, cy - 0.5, vx * 0.2, -40, here, dt2, df);
      return;
    }

    // Genuinely nowhere to go and nothing to displace (fully packed solid).
    // Deposit the heat so at least the energy survives the loss.
    w.addTemp(cx, cy, (t - w.getTemp(cx, cy)) * 0.25);
  }

  private remove(i: number): void {
    const last = --this.count;
    if (i !== last) {
      this.x[i] = this.x[last];
      this.y[i] = this.y[last];
      this.vx[i] = this.vx[last];
      this.vy[i] = this.vy[last];
      this.mat[i] = this.mat[last];
      this.temp[i] = this.temp[last];
      this.flags[i] = this.flags[last];
      this.life[i] = this.life[last];
    }
  }

  clear(): void {
    this.count = 0;
  }
}
